<template>
    <div class="library-folder-list" :data-document-folder-id="parentId === null ? 'root' : parentId">
        <draggable :value="folders" handle=".library-folder-row" filter=".library-menu-button" :prevent-on-filter="false" :fallback-tolerance="5" :force-fallback="true" @start="setDragging(true)" @end="setDragging(false)" :disabled="!canReorder" :animation="150" ghost-class="sortable-ghost"
            @input="$emit('reorder-folders', { folders: $event, parentId })">
            <div v-for="folder in folders" :key="folder.id" class="library-folder-group">
                <div class="library-folder-row" :data-document-folder-id="folder.id" @dragover="dragOver" @drop="dropInto($event, folder.id)" :class="{ 'library-folder-selected': selectedFolderId === folder.id, 'library-row-draggable': canReorder }">
                    <button type="button" class="library-folder-toggle" :disabled="busy" :aria-expanded="isOpen(folder.id) ? 'true' : 'false'"
                        @click="$emit('toggle', folder)" @keydown.right.prevent="expand(folder)" @keydown.left.prevent="collapse(folder)">
                        <chevron-down-icon v-if="isOpen(folder.id)" size="16" class="flex-none text-gray-600" aria-hidden="true" />
                        <chevron-right-icon v-else size="16" class="flex-none text-gray-400" aria-hidden="true" />
                        <span class="truncate" :class="{ 'library-folder-empty': !folder.documentCount }">{{ folder.name }}</span>
                    </button>
                    <span class="library-count">{{ folder.documentCount }} {{ folder.documentCount === 1 ? 'document' : 'documents' }}</span>
                    <button v-if="canEdit" type="button" class="library-menu-button" :disabled="busy" :aria-label="`Actions for ${folder.name}`" aria-haspopup="menu"
                        @click.stop="$emit('menu', { event: $event, kind: 'folder', item: folder })"><more-horizontal-icon size="16" aria-hidden="true" /></button>
                </div>
                <div v-if="isOpen(folder.id)" class="library-folder-contents">
                    <library-folder-list :folders="folder.children" :documents="folder.documents" :parent-id="folder.id" :open-ids="openIds"
                        :selection-mode="selectionMode" :selected-keys="selectedKeys" @select-document="$emit('select-document', $event)"
                        :selected-folder-id="selectedFolderId" :busy="busy" :can-edit="canEdit" :can-reorder="canReorder" :searching="searching" :can-clone="canClone" :can-add="canAdd" :can-drop="canDrop"
                        @add-document="$emit('add-document', $event)" @drop-document="$emit('drop-document', $event)" @move-document="$emit('move-document', $event)"
                        @toggle="$emit('toggle', $event)" @menu="$emit('menu', $event)"
                        @reorder-folders="$emit('reorder-folders', $event)" @reorder-documents="$emit('reorder-documents', $event)" />
                    <p v-if="!folder.children.length && !folder.documents.length" class="px-3 py-2 text-xs text-gray-500">{{ searching ? 'No matching documents in this folder' : 'No documents' }}</p>
                </div>
            </div>
        </draggable>
        <draggable :value="documents" handle=".library-document-row" filter=".library-menu-button" :prevent-on-filter="false" :fallback-tolerance="5" :force-fallback="true" @start="setDragging(true)" @end="endDocumentDrag" :disabled="!canReorder" :animation="150" ghost-class="sortable-ghost">
            <div v-for="document in documents" :key="documentKey(document)" class="library-document-row" :draggable="canAdd && !!document.add_route" @dragstart="startSourceDrag($event, document)" :class="{ 'library-row-draggable': canReorder }">
                <input v-if="selectionMode" type="checkbox" :checked="selectedKeys.includes(documentKey(document))" :aria-label="`Select ${document.display_filename}`" :disabled="busy" @change="$emit('select-document', document)" />
                <file-icon v-if="!selectionMode" size="14" class="flex-none text-gray-500" aria-hidden="true" />
                <div class="flex-1 min-w-0">
                    <button v-if="selectionMode" type="button" class="library-document-title text-left w-full" :disabled="busy" @click="$emit('select-document', document)">{{ document.display_filename }}</button>
                    <a v-else-if="document.show_route" :href="document.show_route" target="_blank" rel="noopener" :draggable="false" class="library-document-title hover:text-blue-700" :title="document.display_filename">{{ document.display_filename }}</a>
                    <span v-else class="library-document-title" :title="document.display_filename">{{ document.display_filename }}</span>
                    <span v-if="!selectionMode && !document.download_route" class="block text-xs text-gray-500">No downloadable file available</span>
                </div>
                <span class="library-count">{{ document.size_for_humans }}</span>
                <button v-if="!selectionMode && (canEdit || document.download_route || document.show_route || (canClone && document.clone_route))" type="button" class="library-menu-button" :disabled="busy"
                    :aria-label="`Actions for ${document.display_filename}`" aria-haspopup="menu" @click.stop="$emit('menu', { event: $event, kind: 'document', item: document })">
                    <more-horizontal-icon size="16" aria-hidden="true" />
                </button>
                <button v-if="canAdd && document.add_route" type="button" class="library-menu-button library-add-button" :disabled="busy" :aria-label="`Add ${document.display_filename} to transaction`" title="Add to transaction" @click.stop="$emit('add-document', document)"><arrow-right-icon size="16" aria-hidden="true" /></button>
            </div>
        </draggable>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import { ChevronDownIcon, ChevronRightIcon, MoreHorizontalIcon, FileIcon, ArrowRightIcon } from 'vue-feather-icons';

export default {
    name: 'LibraryFolderList',
    components: { draggable, ChevronDownIcon, ChevronRightIcon, MoreHorizontalIcon, FileIcon, ArrowRightIcon },
    props: {
        selectionMode: { type: Boolean, default: false },
        selectedKeys: { type: Array, default: () => [] },
        folders: { type: Array, required: true },
        documents: { type: Array, required: true },
        parentId: { default: null },
        openIds: { type: Array, required: true },
        selectedFolderId: { default: null },
        busy: { type: Boolean, default: false },
        canEdit: { type: Boolean, default: false },
        canReorder: { type: Boolean, default: false },
        searching: { type: Boolean, default: false },
        canAdd: { type: Boolean, default: false },
        canDrop: { type: Boolean, default: false },
        canClone: { type: Boolean, default: false },
    },
    data() { return { dragging: false }; },
    beforeDestroy() { if (this.dragging) this.setDragging(false); },
    methods: {
        documentKey(document) { return `${document.type || 'document'}:${document.id}`; },
        startSourceDrag(event, document) {
            if (!this.canAdd || !document.add_route) return;
            event.stopPropagation(); event.dataTransfer.effectAllowed = 'copy';
            event.dataTransfer.setData('application/x-c2c-library-document', JSON.stringify({ id: document.id, source: document.source_type }));
        },
        dragOver(event) {
            if (this.canDrop && event.dataTransfer.types.includes('application/x-c2c-library-document')) {
                event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = 'copy';
            }
        },
        dropInto(event, folderId) {
            if (!this.canDrop) return;
            const raw = event.dataTransfer.getData('application/x-c2c-library-document');
            if (!raw) return;
            event.preventDefault(); event.stopPropagation(); this.$emit('drop-document', { raw, folderId });
        },
        endDocumentDrag(event) {
            this.setDragging(false);
            if (!this.canReorder) return;
            const dragged = this.documents[event.oldIndex];
            if (!dragged) return;
            const pointer = event.originalEvent?.changedTouches?.[0] || event.originalEvent;
            const tree = this.$el.closest('.library-tree');
            const target = pointer && document.elementFromPoint(pointer.clientX, pointer.clientY);
            if (tree && target && tree.contains(target)) {
                const destination = target.closest('[data-document-folder-id]');
                const raw = destination?.getAttribute('data-document-folder-id');
                const folderId = !raw || raw === 'root' ? null : Number(raw);
                if (folderId !== this.parentId) {
                    this.$emit('move-document', { document: dragged, folderId });
                    return;
                }
            } else {
                return;
            }
            if (event.oldIndex !== event.newIndex && Number.isInteger(event.newIndex)) {
                const documents = [...this.documents];
                documents.splice(event.newIndex, 0, documents.splice(event.oldIndex, 1)[0]);
                this.$emit('reorder-documents', { documents, parentId: this.parentId });
            }
        },
        setDragging(dragging) {
            this.dragging = dragging;
            document.body.classList.toggle('library-reordering', dragging);
        },
        isOpen(id) { return this.searching || this.openIds.includes(id); },
        expand(folder) { if (!this.isOpen(folder.id)) this.$emit('toggle', folder); },
        collapse(folder) { if (this.isOpen(folder.id) && !this.searching) this.$emit('toggle', folder); },
    },
};
</script>

<style scoped>
.library-folder-list { user-select: none; -webkit-user-select: none; }
.library-folder-empty { color: #9ca3af; }
.library-folder-group { border-bottom: 1px solid #e5e7eb; }
.library-folder-group:last-child { border-bottom: 0; }
.library-folder-row, .library-document-row { display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.75rem; }
.library-folder-row { padding: var(--library-folder-padding, 0.35rem 0.75rem); }
.library-folder-row:hover, .library-document-row:hover { background: #eff6ff; }
.library-folder-selected { background: #eff6ff; }
.library-folder-toggle { display: flex; flex: 1; min-width: 0; align-items: center; gap: 0.5rem; padding: var(--library-folder-toggle-padding, 0.4rem 0); font-size: var(--library-folder-font-size, 1rem); text-align: left; color: #374151; }
.library-folder-toggle[aria-expanded="true"] { color: #2563eb; }
.library-folder-contents { margin-left: 1.25rem; padding: 0.1rem 0 0.35rem 0.5rem; border-left: 1px solid #e5e7eb; }
.library-document-row { padding: var(--library-document-padding, 0.35rem 0.75rem); min-height: var(--library-document-min-height, 2.5rem); border-bottom: 1px solid #e5e7eb; }
.library-document-row:last-child { border-bottom: 0; }
.library-document-title { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 0.875rem; font-weight: 600; color: #374151; }
.library-count { flex-shrink: 0; font-size: 0.75rem; color: #6b7280; }
.library-menu-button { margin-left: 0.5rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 1.75rem; height: 1.75rem; border-radius: 9999px; border: 1px solid #d1d5db; background: #fff; color: #6b7280; }
.library-menu-button + .library-add-button { margin-left: -0.125rem; }
.library-menu-button:hover { background: #e5e7eb; color: #2563eb; }
.library-row-draggable, .library-row-draggable .library-folder-toggle, .library-row-draggable .library-document-title { cursor: default; }
.sortable-ghost { opacity: 0.5; background: #eff6ff; }
button:disabled { opacity: 0.5; cursor: default; }
button:focus-visible, a:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.library-folder-toggle:focus { outline: none; box-shadow: none; }
@media (max-width: 640px) {
    .library-folder-contents { margin-left: 0.6rem; padding-left: 0.25rem; }
    .library-folder-row, .library-document-row { padding-left: 0.5rem; padding-right: 0.5rem; }
}
</style>

<style>
body.library-reordering, body.library-reordering * { cursor: move !important; }
</style>
